import { computed, watch } from 'vue';
import { enterViewAsUserMode, exitViewAsUserMode } from '@cognitoforms/api/services/settings/view-as-user-service';
import session, { refreshSession } from '@cognitoforms/api/services/session';
import { globalStore } from 'src/stores/global-store';
import notify from 'src/utilities/notify';

export const useViewAsUser = () => {
	const initViewAsUser = () => {
		if (globalStore.originalUser.UserId && globalStore.originalUser.UserId !== globalStore.user.Id)
			globalStore.setIsInViewAsUserMode(true);
	};

	const redirectUser = (isEntering: boolean, location: string = null) => {
		const orgCode = globalStore.organization.Code;
		if (isEntering) {
			window.location.assign(`/${orgCode}/home`);
			if (location)
				localStorage.setItem(`viewAsUserLocation-${orgCode}`, location);
		}
		else {
			// Remove beforeUnload event handler so Cognito.onNavigate is not triggered
			const $ = (window as any).$;
			if ($)
				$(window).off('beforeunload');

			window.location.assign(`/${orgCode}` + (localStorage.getItem(`viewAsUserLocation-${orgCode}`) ?? '/settings/users'));
		}
	};

	const isInViewAsUserModeOnServer = async () => {
		await refreshSession();
		return !!session.value?.originalUser;
	};

	const handleEnterViewAsUserMode = async (id: string, location: string = null) => {
		await enterViewAsUserMode(id).then(() => {
			redirectUser(true, location);
		});
	};

	const handleExitViewAsUserMode = async (showToast: boolean = false) => {
		await exitViewAsUserMode().then(() => {
			globalStore.setIsInViewAsUserMode(false);

			if (showToast)
				localStorage.setItem('viewAsRedirect', 'true');

			redirectUser(false);
		});
	};

	const showErrorNotification = async () => {
		notify('You cannot make changes while viewing as another user.', 'warning');
	};

	// handle displayed user role updating
	watch(() => globalStore.organization.Role, (newRole) => {
		if (globalStore.isInViewAsUserMode) {
			if (newRole === 'Owner' && globalStore.originalUser.Role === 'Administrator')
				handleExitViewAsUserMode(true);
		}
	});

	// handle original user's role updating
	watch(() => globalStore.originalUser.Role, (newRole) => {
		if (globalStore.isInViewAsUserMode) {
			if (newRole === 'Editor' || newRole === 'Reviewer' || newRole === 'LimitedAccess')
				handleExitViewAsUserMode(true);
			if (newRole === 'Administrator' && globalStore.organization.Role === 'Owner')
				handleExitViewAsUserMode(true);
		}
	});

	globalStore.isReady.then(() => {
		// Initialize view as state
		initViewAsUser();
	});

	return {
		orgName: computed(() => globalStore.organization.Name),
		handleEnterViewAsUserMode,
		handleExitViewAsUserMode,
		showErrorNotification,
		redirectUser,
		isInViewAsUserModeOnServer
	};
};

export type ViewAsUser = ReturnType<typeof useViewAsUser>;